import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { 
  StoreIntegration, 
  CreateStoreIntegrationDto, 
  UpdateStoreIntegrationDto,
  StoreIntegrationResponse,
  StoreIntegrationListResponse
} from 'app/models/store-integration.models';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StoreIntegrationService {
  
  private apiUrl = `${environment.apiUrl}store-integrations`;
  
  constructor(private http: HttpClient) {}

  /**
   * Obtiene todas las integraciones asociadas a una tienda específica
   * Usa el endpoint: GET /store-integrations/{storeId}
   */
  getStoreIntegrations(storeId: number): Observable<StoreIntegration[]> {
    return this.http.get<any>(`${this.apiUrl}/${storeId}`)
      .pipe(
        map(response => {
          console.log('Store integrations response:', response);
          
          // Manejar diferentes formatos de respuesta
          if (response && response.success && response.data) {
            // Formato envuelto
            return Array.isArray(response.data) ? response.data : [response.data];
          } else if (Array.isArray(response)) {
            // Array directo
            return response;
          } else if (response && response.id) {
            // Objeto único - convertir a array
            return [response];
          }
          return [];
        }),
        catchError(error => {
          console.error('Error loading store integrations:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Obtiene una integración específica de una tienda
   */
  getStoreIntegration(storeId: number, integrationId: number): Observable<StoreIntegration | null> {
    return this.http.get<any>(`${this.apiUrl}/${storeId}/${integrationId}`)
      .pipe(
        map(response => {
          // Manejar ambos formatos: envuelto y directo
          if (response && response.success && response.data) {
            return response.data;
          } else if (response && response.id) {
            // Respuesta directa del objeto
            return response;
          }
          return null;
        }),
        catchError(error => {
          console.error('Error loading store integration:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Crea una nueva asociación entre tienda e integración (POST)
   */
  createStoreIntegration(createDto: CreateStoreIntegrationDto): Observable<StoreIntegration> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<StoreIntegration>(this.apiUrl, createDto, { headers })
      .pipe(
        map(response => {
          // El servidor retorna directamente el objeto StoreIntegration
          console.log('Store integration created successfully:', response);
          return response;
        }),
        catchError(error => {
          console.error('Error creating store integration:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Actualiza una asociación existente (PATCH)
   */
  updateStoreIntegration(
    storeId: number, 
    integrationId: number, 
    updateDto: UpdateStoreIntegrationDto
  ): Observable<StoreIntegration> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.patch<StoreIntegrationResponse>(
      `${this.apiUrl}/${storeId}/${integrationId}`, 
      updateDto, 
      { headers }
    ).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Error updating store integration');
      }),
      catchError(error => {
        console.error('Error updating store integration:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina una asociación entre tienda e integración (DELETE)
   */
  deleteStoreIntegration(storeId: number, integrationId: number): Observable<boolean> {
    return this.http.delete<StoreIntegrationResponse>(`${this.apiUrl}/${storeId}/${integrationId}`)
      .pipe(
        map(response => {
          return response.success;
        }),
        catchError(error => {
          console.error('Error deleting store integration:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Activa o desactiva una integración de tienda
   */
  toggleStoreIntegration(
    storeId: number, 
    integrationId: number, 
    isActive: boolean
  ): Observable<StoreIntegration> {
    return this.updateStoreIntegration(storeId, integrationId, { isActive });
  }

  /**
   * Actualiza solo la configuración de una integración
   */
  updateStoreIntegrationConfiguration(
    storeId: number,
    integrationId: number,
    configuration: any
  ): Observable<StoreIntegration> {
    return this.updateStoreIntegration(storeId, integrationId, { configuration });
  }

  /**
   * Obtiene todas las integraciones disponibles (sin relación con tienda específica)
   */
  getAvailableIntegrations(): Observable<any[]> {
    // Este endpoint devuelve todas las integraciones disponibles del sistema
    return this.http.get<any>(`${environment.apiUrl}integrations`)
      .pipe(
        map(response => {
          if (response.success || Array.isArray(response)) {
            return Array.isArray(response) ? response : response.data || [];
          }
          return [];
        }),
        catchError(error => {
          console.error('Error loading available integrations:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Obtiene las integraciones de una tienda con información extendida
   * Usa el endpoint: GET /store-integrations/{storeId}
   */
  getStoreIntegrationsWithDetails(storeId: number): Observable<StoreIntegration[]> {
    return this.http.get<any>(`${this.apiUrl}/${storeId}`)
      .pipe(
        map(response => {
          // Manejar diferentes formatos de respuesta
          if (response && response.success && response.data) {
            // Formato envuelto
            return Array.isArray(response.data) ? response.data : [response.data];
          } else if (Array.isArray(response)) {
            // Array directo
            return response;
          } else if (response && response.id && response.integration) {
            // Objeto único con detalles - convertir a array
            console.log('🔄 Convirtiendo objeto único a array:', response);
            return [response];
          }
          return [];
        }),
        catchError(error => {
          console.error('Error loading store integrations with details:', error);
          return throwError(() => error);
        })
      );
  }
}